import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { ReactNode, useRef, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from "react-hook-form";

import { setControllerFieldRef } from "../../helpers/form";
import { getNestedProperty } from "../../helpers/object";
import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";
import { APButtonLink } from "../buttons/APButtonLink";
import APIconButton from "../buttons/APIconButton";

export type AutoCompleteOption = {
  label: string;
  id: string;
};

interface APAutoCompleteProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  options: AutoCompleteOption[];
  validations: ValidationOptions;
  errors: FieldErrors<T>;
  defaultValue: PathValue<T, Path<T>> | undefined;
  showRequired?: boolean;
  highlightOnEmpty?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  hiddenOptions?: string[];
  multiple?: boolean;
  setValue?: UseFormSetValue<T>;
  hidden?: boolean;
  maxSelected?: number;
  disableSelectAll?: boolean;
  hideClearButton?: boolean;
  endAdornment?: ReactNode;
}

const APAutoComplete = <T extends FieldValues>({
  name,
  control,
  label,
  options,
  validations,
  errors,
  defaultValue,
  showRequired,
  highlightOnEmpty,
  readOnly,
  disabled,
  hiddenOptions,
  multiple,
  setValue,
  hidden,
  maxSelected,
  disableSelectAll,
  hideClearButton,
  endAdornment,
}: APAutoCompleteProps<T>) => {
  const error = getNestedProperty(name, errors);
  const selectMenuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const onFocus = () => {
    const menuElement = selectMenuRef?.current?.children[2]?.children[0]
      ?.firstChild as HTMLElement | null;

    if (menuElement) {
      menuElement.focus();
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={validationOptionsToControlRules(label, validations)}
      defaultValue={defaultValue}
      render={({ field }) => {
        if (options.length > 15) {
          return (
            <Autocomplete
              disablePortal
              freeSolo={false}
              multiple={multiple}
              disableCloseOnSelect={multiple}
              id={name}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              options={options.filter(
                (option) =>
                  !hiddenOptions || hiddenOptions.indexOf(option.id) === -1
              )}
              readOnly={readOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={(el) => setControllerFieldRef(field.ref, el)}
                  required={validations.required}
                  sx={{ backgroundColor: "white" }}
                  label={showRequired ? `${label} *` : label}
                  error={!!error || (highlightOnEmpty && !field.value)}
                  helperText={`${error?.message || ""}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => {
                const { id, ...optionProps } = props;

                return (
                  <li {...optionProps} key={id}>
                    {multiple && (
                      <Box sx={{ display: "flex", width: "1rem", mr: 1 }}>
                        {selected && (
                          <CheckIcon
                            sx={{
                              width: "inherit",
                              height: "inherit",
                              color: "primary.main",
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {option.label}
                  </li>
                );
              }}
              value={field.value}
              onBlur={field.onBlur}
              onChange={(event, value) => {
                field.onChange(value);
              }}
              disabled={disabled}
              sx={{
                display: hidden ? "none" : undefined,
              }}
            />
          );
        } else {
          return (
            <FormControl
              id={name}
              fullWidth
              error={!!error || (highlightOnEmpty && !field.value)}
              disabled={disabled}
              sx={{
                display: hidden ? "none" : undefined,
              }}
            >
              <InputLabel>
                {showRequired || validations.required ? `${label} *` : label}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                inputRef={(el) => setControllerFieldRef(field.ref, el)}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={
                  multiple
                    ? (field.value || []).map(
                        (item: AutoCompleteOption) => item.id
                      )
                    : field.value?.id || ""
                }
                renderValue={
                  multiple
                    ? (selected) => {
                        return (
                          <Box>
                            <Stack gap={1} direction="row" flexWrap="wrap">
                              {selected.map((id: string) => {
                                const selectedItem = options.find(
                                  (option) => option.id === id
                                );
                                return (
                                  <Chip
                                    key={id}
                                    label={selectedItem?.label}
                                    onDelete={() => {
                                      const newValue = field.value.filter(
                                        (item: AutoCompleteOption) =>
                                          item.id !== id
                                      );
                                      setValue &&
                                        setValue(field.name, newValue);
                                    }}
                                    deleteIcon={
                                      <CancelIcon
                                        onMouseDown={(event) =>
                                          event.stopPropagation()
                                        }
                                      />
                                    }
                                  />
                                );
                              })}
                            </Stack>
                            <Box
                              sx={{
                                display: "flex",
                                columnGap: 1,
                                mr: 1,
                                mt: field.value.length > 0 ? 0.5 : -0.75,
                                mb: -0.75,
                                justifyContent: "end",
                              }}
                            >
                              <Button
                                size="small"
                                color="primary"
                                variant="text"
                                disableElevation={true}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onClick={() => {
                                  field.onChange([]);
                                }}
                                disabled={field.value.length === 0}
                              >
                                Clear
                              </Button>
                              {!disableSelectAll && (
                                <Button
                                  size="small"
                                  color="primary"
                                  variant="text"
                                  disableElevation={true}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                  onClick={() => field.onChange([...options])}
                                  disabled={
                                    field.value.length === options.length
                                  }
                                >
                                  Select All
                                </Button>
                              )}
                            </Box>
                          </Box>
                        );
                      }
                    : undefined
                }
                label={
                  showRequired || validations.required ? `${label} *` : label
                }
                readOnly={readOnly}
                required={validations.required}
                onBlur={field.onBlur}
                sx={{
                  backgroundColor: "white",
                  paddingRight: "0",
                  // Hide the icon button by default
                  "& .clear-btn": {
                    visibility: "hidden",
                  },
                  // Show the icon button when the Select container is hovered
                  "&:hover .clear-btn": {
                    visibility: "visible",
                  },
                }}
                onChange={(event) => {
                  const value = event.target.value;

                  if (multiple) {
                    // If multi select, check if item is being removed
                    const itemRemoved = value.length < field.value.length;

                    if (itemRemoved) {
                      const newValue = field.value.filter(
                        (option: AutoCompleteOption) =>
                          value.includes(option.id)
                      );

                      field.onChange(newValue);
                    } else {
                      // Check if max selected is reached
                      if (maxSelected && field.value.length >= maxSelected) {
                        return;
                      }

                      // Item is being added
                      const newId = value[value.length - 1];

                      if (newId) {
                        const option = options.find(
                          (option) => option.id === newId
                        );
                        const newValue = {
                          id: newId,
                          label: option?.label ?? "",
                        };

                        field.onChange([...field.value, newValue]);
                      }
                    }
                  } else {
                    // If single selection
                    const option = options.find(
                      (option) => option.id === value
                    );
                    const newValue = { id: value, label: option?.label ?? "" };

                    field.onChange(newValue);
                  }
                }}
                MenuProps={{
                  disableAutoFocus: true,
                  MenuListProps: {
                    autoFocusItem: false,
                  },
                  ref: selectMenuRef,
                  TransitionProps: {
                    onEntered: onFocus,
                  },
                }}
                multiple={multiple}
                displayEmpty
                endAdornment={
                  <Box
                    sx={{
                      mr: 2.5,
                      display: "flex",
                      alignItems: "center",
                      position: "absolute",
                      right: "12px",
                      gap: 1,
                    }}
                  >
                    {field.value &&
                      !multiple &&
                      !readOnly &&
                      !disabled &&
                      !hideClearButton && (
                        <Box className="clear-btn" sx={{ opacity: 0.65 }}>
                          <APIconButton
                            size="small"
                            iconName="close"
                            onClick={() => field.onChange(null)}
                          />
                        </Box>
                      )}
                    {endAdornment ? <Box>{endAdornment}</Box> : null}
                  </Box>
                }
              >
                {multiple && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      p: 2,
                      position: "sticky",
                      top: 0,
                      background: "white",
                      right: 0,
                      zIndex: 1,
                    }}
                  >
                    <APButtonLink
                      text="Done"
                      onClick={() => setOpen(false)}
                      noUnderline={true}
                    />
                  </Box>
                )}
                {options.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    sx={{
                      display:
                        hiddenOptions && hiddenOptions.indexOf(option.id) !== -1
                          ? "none"
                          : "flex",
                      alignItems: "center",
                    }}
                  >
                    {multiple && (
                      <Box sx={{ display: "flex", width: "1rem", mr: 1 }}>
                        {field.value.some(
                          (item: AutoCompleteOption) => item.id === option.id
                        ) && (
                          <CheckIcon
                            sx={{
                              width: "inherit",
                              height: "inherit",
                              color: "primary.main",
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{`${error?.message || ""}`}</FormHelperText>
            </FormControl>
          );
        }
      }}
    />
  );
};

export default APAutoComplete;
